import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Flex,
    HStack,
    IconButton,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverFooter,
    PopoverHeader,
    Spinner,
    Stack,
    Text,
    Textarea,
    Tooltip,
    useColorModeValue
} from "@chakra-ui/react";
import { MdCheck, MdLightbulb, MdRefresh } from "react-icons/md";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { inferenceService } from "services/inferenceService";

const ProcessDecisionInference = ({ onClose, onUseSuggestion, canInfer, loadingSuggestion, setLoadingSuggestion, data, onSuggestionRequested }) => {
    const { t } = useTranslation();
    const toast = useCustomToast();
    const [suggestion, setSuggestion] = useState(null);
    const borderColor = useColorModeValue("gray.400", "gray.600");
    const textValue = suggestion ? suggestion : t("process.judgment.no-suggestion");
    const textareaRef = useRef(null);

    const fetchSuggestion = async () => {
        setLoadingSuggestion(true);
        try {
            const response = await inferenceService.infer(toDto(data));
            setSuggestion(response);
            onSuggestionRequested(true);
        } catch (error) {
            toast({
                title: error || t("process.messages.errors.infer"),
                status: "error",
            });
            setSuggestion(null);
            onSuggestionRequested(false);
        } finally {
            setLoadingSuggestion(false);
        }
    };

    const toDto = (data) => ({
        fatos: data?.fatos?.value ?? "",
        fundamentos: data?.fundamentos?.value ?? "",
    });

    const handleUseSuggestionClick = () => {
        onUseSuggestion(suggestion);
        onClose();
    };

    const handleSuggestAgain = () => {
        fetchSuggestion();
    };

    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = "auto";
            textarea.style.height = textarea.scrollHeight + "px";
        }
    }, [textValue]);

    return (
        <>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader>
                <HStack spacing={2}>
                    <span>{t("process.judgment.suggestion-title")}</span>
                </HStack>
            </PopoverHeader>
            <PopoverBody>
                {loadingSuggestion ? (
                    <Flex direction="column" align="center" justify="center" height="100%">
                        <Spinner size="xl" />
                        <Text>{t("process.judgment.loading-suggestion")}</Text>
                    </Flex>
                ) : (
                    <Textarea
                        ref={textareaRef}
                        value={textValue}
                        readOnly
                        width="100%"
                        resize="vertical"
                        overflow="hidden"
                    />
                )}
            </PopoverBody>
            <PopoverFooter>
                <Stack direction="row" justify="space-between" align="center" w="100%">
                    <Tooltip
                        label={
                            canInfer
                                ? t("process.judgment.suggest")
                                : t("process.judgment.suggest-invalid")
                        }
                    >
                        <IconButton
                            size="sm"
                            icon={
                                suggestion
                                    ? <MdRefresh />
                                    : <MdLightbulb />
                            }
                            onClick={handleSuggestAgain}
                            border={`solid 0.1em ${borderColor}`}
                            isDisabled={loadingSuggestion || !canInfer}
                        />
                    </Tooltip>

                    <Tooltip label={t("process.judgment.use-suggestion")}>
                        <IconButton
                            size="sm"
                            icon={<MdCheck />}
                            colorScheme="blue"
                            onClick={handleUseSuggestionClick}
                            isDisabled={!suggestion || loadingSuggestion}
                            aria-label={t("process.judgment.use-suggestion")}
                        />
                    </Tooltip>
                </Stack>
            </PopoverFooter>
        </>
    );
};

export default ProcessDecisionInference;